import React, { useEffect, useMemo, useState } from 'react';
import Slider from 'react-rangeslider';
import 'react-rangeslider/lib/index.css';
import clsx from 'clsx';
import URLs from '../../utils/url';
import './index.scss';
import If from '../If';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getBestSellerProduct } from '../../redux/actions/customizer';
import VideoWithFallback from '../VideoWithFallback';
import { getFontOptions, getSelectedOption } from '../../utils/commonRingUtils';
import EngravingFilter from '../EngravingFilter';
import Accordion from '../Accordion';
import LoaderIcon from '../../assets/images/loader2inv.gif';

type IProps = {
  id: string;
  handleBestSeller: (id: string) => void;
  engraving: string;
  setEngraving: (text: string) => void;
  handleBestSellerDone: (size?: number) => void;
  handleCustomize: (fingerSizeSku?: string) => void;
  product: any;
  navigateToUrl: (url: string, replace?: boolean) => void;
  isOpen: boolean;
  showMagicNumber?: boolean;
  initialSize?: number;
  onClose: () => void;
  onLoadFailed?: () => void;
};

function DiscountedPopupLoadingPlaceholder() {
  return (
    <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-min-h-[300px]" aria-hidden>
      <img src={LoaderIcon} alt="Loading" className="tw-opacity-50" />
    </div>
  );
}

let timedOutId: ReturnType<typeof setTimeout> | undefined;

// The decision tree list: the bestseller itself, then its similar rings,
// cheapest first.
//
// Exported because the modal's width now depends on how many cards this produces, and that
// width is set on the AppModal in pages/index.tsx, outside this component. The count is
// 1 + similar_rings.length, and re-deriving that arithmetic at the call site is how the two
// drift apart.
export const getStartingPointRings = (bestSellerProduct: any) => {
  if (!bestSellerProduct?.show_similar_rings || !(bestSellerProduct?.similar_rings?.length > 0)) return [];
  const rings = [
    {
      price: bestSellerProduct?.price,
      image_url: `${URLs.assetsBaseUrl}${bestSellerProduct?.images?.[0]}`,
      product_url: bestSellerProduct?.ring_options_url,
      title: bestSellerProduct?.title || bestSellerProduct?.description,
      // The API sends highlights on the similar rings but not on the bestseller itself.
      highlights: bestSellerProduct?.data?.highlights
    },
    ...(bestSellerProduct?.similar_rings ?? [])
  ];
  const parsePrice = (price: any) => parseFloat(String(price ?? '').replace(/[^0-9.]/g, '')) || 0;
  return rings.sort((a, b) => parsePrice(a?.price) - parsePrice(b?.price));
};

// Exactly three rings stack in a single column, matching the other builders; any other count
// keeps the two-column grid, where three would wrap to an uneven 2 + 1. The card itself needs
// no change — a full-width row is the most space its side-by-side image and text ever get.
//
// One definition, two consumers: the grid below reads it for its column count, and
// pages/index.tsx reads it to narrow the modal, since 850px sized for two columns strands a
// card's worth of whitespace beside one.
export const isSingleColumnStartingPoint = (rings: any[]) => rings.length === 3;

const DiscountedPopup = ({
  id,
  handleBestSeller,
  product,
  navigateToUrl,
  engraving,
  setEngraving,
  handleBestSellerDone,
  handleCustomize,
  isOpen,
  showMagicNumber,
  initialSize,
  onClose,
  onLoadFailed
}: IProps) => {
  const { bestSellerProduct } = useAppSelector((state) => state.customizer);
  const dispatch = useAppDispatch();
  const [sliderValue, setSliderValue] = useState(initialSize ?? 10);
  const [isProceeding, setIsProceeding] = useState(false);

  const handleProceed = async () => {
    setIsProceeding(true);
    try {
      await handleBestSellerDone(sliderValue);
    } finally {
      setIsProceeding(false);
    }
  };

  // The builder keys finger size off the option's sku (not the numeric slider
  // value), so map the current slider value back to its option sku.
  const getSelectedSizeSku = (): string | undefined => {
    const sizeOptions: any[] = bestSellerProduct?.finger_size || bestSellerProduct?.size || [];
    const match = sizeOptions.find((opt) => Math.abs(parseFloat(opt.sku) - sliderValue) < 0.001);
    return match?.sku;
  };

  useEffect(() => {
    if (!id) return;
    if (!bestSellerProduct || bestSellerProduct?.bestseller_id !== id) {
      fetchBestSellerProduct(undefined, { isInitialLoad: true });
    }
  }, [id]);

  // Only an initial-load failure means the id is bad. A failed size/engraving refetch still
  // has the previously loaded product on screen, so it must not tear the modal down.
  const fetchBestSellerProduct = async (
    data?: { size: number; engraving: string },
    options?: { isInitialLoad?: boolean }
  ) => {
    const { size, engraving } = data || {};
    try {
      const payload = {
        id,
        data: {
          size: size || '',
          engraving: engraving || ''
        }
      };
      const result = await dispatch(getBestSellerProduct(payload)).unwrap();
      // Covers a 200 that comes back without the id we asked for, alongside the 404 below.
      if (options?.isInitialLoad && String(result?.bestseller_id ?? '') !== String(id)) {
        onLoadFailed?.();
      }
    } catch (error) {
      console.log(error);
      if (options?.isInitialLoad) {
        onLoadFailed?.();
      }
    }
  };

  const handleClick = (id: string) => () => {
    handleBestSeller(id);
  };

  const handleClickSimilarRing = (ring: any) => {
    if (ring?.product_url) {
      const url = String(ring.product_url).replace(/^\/shop/, '');
      navigateToUrl(url, false);
    }
    onClose();
  };

  const handleOnChange = (updated: any) => {
    if (updated.engraving_new?.includes('None')) {
      setEngraving('');
      fetchBestSellerProduct({ size: sliderValue, engraving: '' });
    } else {
      setEngraving(updated.engraving_new);
      fetchBestSellerProduct({ size: sliderValue, engraving: updated.engraving_new });
    }
  };

  const getMinRange = (list: any[]) => (list && list.length > 0 ? parseFloat(list[0].sku) : 3);

  const getMaxRange = (list: any[]) => (list && list.length > 0 ? parseFloat(list[list.length - 1].sku) : 24);

  const getStep = (list: any[]) => (list && list.length > 0 ? parseFloat(list[1].sku) - parseFloat(list[0].sku) : 0.25);

  const handleSliderChange = (value: number) => {
    setSliderValue(value);
    if (timedOutId) {
      clearTimeout(timedOutId);
    }
    timedOutId = setTimeout(() => {
      fetchBestSellerProduct({ size: value, engraving: engraving });
    }, 1000);
  };

  const getEngravingContent = () => {
    const content = {
      hasSwatch: false,
      fieldType: 'engraving_new',
      options: getFontOptions(
        bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []
      ),
      selectedOption: getSelectedOption(
        bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []
      ),
      engravingText: bestSellerProduct?.engraving_new?.text?.text || ''
    };
    return (
      <div className="tw-w-full tw-h-full tw-bg-blue-50">
        <EngravingFilter onChange={handleOnChange} isFetching={false} content={content} />
      </div>
    );
  };

  const canShowSimilarRings = useMemo(() => {
    return bestSellerProduct?.show_similar_rings && bestSellerProduct?.similar_rings?.length > 0;
  }, [bestSellerProduct]);

  const similar_rings = useMemo(() => getStartingPointRings(bestSellerProduct), [bestSellerProduct]);

  const isOneColumn = isSingleColumnStartingPoint(similar_rings);

  const showLoadingPlaceholder = Boolean(
    isOpen && id && (!bestSellerProduct || String(bestSellerProduct?.bestseller_id ?? '') !== String(id))
  );

  const isEngravingEnabled = bestSellerProduct?.is_engraving_enabled;

  return (
    <div className="-tw-my-5 tw-p-5 xl:tw-p-8 tw-relative tw-flex tw-flex-col tw-min-h-[300px] tw-max-h-[90vh]">
      <If condition={Boolean(canShowSimilarRings && !!bestSellerProduct && !showLoadingPlaceholder)}>
        <div className="tw-w-full">
          <h4 className="tw-font-reckless tw-text-3xl tw-font-bold tw-text-primary-color tw-mb-2">
            Choose Your Starting Point
          </h4>
          <p className="tw-font-reckless tw-text-base tw-text-gray-600 tw-mt-0 tw-mb-6">
            Pick an option below, then customize every detail
          </p>
          <div className={clsx('tw-grid tw-grid-cols-1 tw-gap-4', { 'md:tw-grid-cols-2': !isOneColumn })}>
            {similar_rings?.map((ring: any) => (
              <button
                key={ring?.product_url}
                type="button"
                onClick={() => handleClickSimilarRing(ring)}
                className="similar-ring-card"
              >
                <div className="tw-h-[135px] tw-flex tw-items-center tw-justify-center tw-shrink-0">
                  <img
                    src={ring?.image_url}
                    alt="Similar ring"
                    className="tw-max-w-full tw-max-h-full tw-object-contain"
                  />
                </div>
                <div className="tw-flex tw-flex-col tw-items-start tw-text-left tw-gap-5">
                  <div className="tw-text-xl tw-font-semibold tw-leading-none tw-text-black">
                    {(() => {
                      const [dollars, cents] = String(ring?.price ?? '').split('.');
                      return (
                        <>
                          {dollars}
                          {cents !== undefined && <sup className="tw-text-xs tw-font-semibold tw-ml-0.5">.{cents}</sup>}
                        </>
                      );
                    })()}
                  </div>
                  <ul className="tw-list-disc tw-pl-4 tw-flex tw-flex-col tw-gap-1 tw-flex-1">
                    {ring?.highlights?.map((highlight: any, index: number) => (
                      <li key={index} className="tw-text-black tw-text-sm tw-leading-tight tw-capitalize">
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              </button>
            ))}
          </div>
        </div>
      </If>
      <If condition={Boolean(!canShowSimilarRings && !!bestSellerProduct && !showLoadingPlaceholder)}>
        <div className="tw-flex tw-flex-wrap tw-gap-4 xl:tw-gap-8">
          <div className="tw-w-full lg:tw-flex-1 shadowBox">
            <div className="tw-px-5">
              <h4 className="tw-font-reckless tw-text-center tw-text-xl tw-font-bold">Ready to Ship</h4>
              <div className="tw-flex tw-justify-center tw-my-3">
                <img
                  src={`${URLs.assetsBaseUrl}${bestSellerProduct?.images[0]}`}
                  alt="ring"
                  className="tw-h-52 tw-object-cover"
                />
              </div>
              <If condition={bestSellerProduct?.variants?.length > 1}>
                <div className="tw-flex tw-flex-col sm:tw-flex-row  tw-flex-wrap tw-justify-center tw-gap-3 tw-mb-8">
                  {bestSellerProduct?.variants?.map((variant: any) => (
                    <button
                      key={variant.bestseller_id}
                      onClick={!variant.selected ? handleClick(variant.bestseller_id) : undefined}
                      className={clsx('tw-text-sm', {
                        'solid-border': !variant.selected,
                        'dotted-border': variant.selected
                      })}
                    >
                      {variant.name}
                    </button>
                  ))}
                </div>
              </If>
              <div className="tw-text-primary-color">{bestSellerProduct?.description}</div>
              <div className="tw-my-5">
                <span className="tw-text-brand-color tw-line-through">{bestSellerProduct?.msrp}</span>
                <span className="tw-text-primary-color tw-pl-2">{bestSellerProduct?.price}</span>
                <span className="tw-text-primary-color tw-italic tw-pl-2">in stock discount</span>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-2">
                <div className="tw-text-primary-color tw-text-base tw-font-bold">
                  Estimate your Size : <b>{sliderValue}</b>
                </div>
                <Slider
                  min={getMinRange(bestSellerProduct?.finger_size || bestSellerProduct?.size)}
                  max={getMaxRange(bestSellerProduct?.finger_size || bestSellerProduct?.size)}
                  step={getStep(bestSellerProduct?.finger_size || bestSellerProduct?.size)}
                  value={sliderValue}
                  tooltip={true}
                  onChange={handleSliderChange}
                />
              </div>
              <If
                condition={
                  (bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []).length >
                  0
                }
              >
                <div className="tw-flex tw-flex-col tw-gap-2 -tw-mx-4">
                  <If condition={Boolean(!isEngravingEnabled)}>
                    <Accordion
                      title="Choose Engraving :"
                      isJXSContent
                      content={getEngravingContent()}
                      height="max-content"
                      defaultOpen={Boolean(bestSellerProduct?.engraving_new?.text?.text)}
                    />
                  </If>
                </div>
              </If>
            </div>
            <div className="tw-mt-5">
              <If condition={Boolean(showMagicNumber)}>
                <button className="hover:tw-bg-brand-color hover:tw-text-tertiary-color tw-block tw-bg-brand-color tw-font-medium tw-px-9 tw-py-2 tw-rounded-tertiary tw-text-tertiary-color tw-w-full tw-text-center">
                  Magic Number: {bestSellerProduct?.magic_number}
                </button>
              </If>
              <If condition={!showMagicNumber}>
                <button
                  disabled={isProceeding}
                  className="hover:tw-bg-[var(--cta-hover-bg-color)] hover:tw-text-[var(--cta-hover-text-color)] tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-brand-color tw-font-medium tw-px-9 tw-py-2 tw-rounded-tertiary tw-text-tertiary-color tw-w-full tw-text-center disabled:tw-opacity-70 disabled:tw-cursor-not-allowed"
                  onClick={handleProceed}
                >
                  {isProceeding ? (
                    <>
                      <span className="btn-spinner" aria-hidden="true" />
                      Processing...
                    </>
                  ) : (
                    'Proceed As Is'
                  )}
                </button>
              </If>
            </div>
          </div>
          <div className="tw-w-full lg:tw-flex-1 shadowBox">
            <h4 className="tw-font-reckless tw-text-center tw-text-xl tw-font-bold">Make it Your Own</h4>
            <div className="tw-flex-1 tw-my-3">
              <VideoWithFallback videoUrl={bestSellerProduct?.video_url} className="discounted-video-box" />
            </div>
            <div className="tw-mt-5">
              <button
                onClick={() => handleCustomize(getSelectedSizeSku())}
                className="hover:tw-bg-[var(--cta-hover-bg-color)] hover:tw-text-[var(--cta-hover-text-color)] tw-block tw-bg-brand-color tw-font-medium tw-px-9 tw-py-2 tw-rounded-tertiary tw-text-tertiary-color tw-w-full tw-text-center"
              >
                Customize in Lashbrook Builder
              </button>
            </div>
          </div>
        </div>
      </If>
      <If condition={showLoadingPlaceholder}>
        <DiscountedPopupLoadingPlaceholder />
      </If>
    </div>
  );
};

export default DiscountedPopup;
